/**
 * 蜜糖黄 · 阳光黄页 独立页脚
 *
 * 与侧栏同属炭黑深色体系，顶部一条琥珀黄细线收口，形成「黄页册」的视觉闭环。
 * 布局：品牌介绍 + 导航列(优先 footer_menus，否则默认双列) + 联系方式列 + 版权条
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

const DEFAULT_MENUS = [
  { name: "首页", href: "/" },
  { name: "发布信息", href: "/publish" },
  { name: "会员中心", href: "/dashboard" },
  { name: "帮助中心", href: "/page/help" },
  { name: "关于我们", href: "/page/about" },
  { name: "隐私政策", href: "/page/privacy" },
  { name: "服务协议", href: "/page/register-agreement" },
];

const FOOTER_INFO_DEFAULT =
  "信息分类检索平台：一键发布、精准分类、快速查找，做您身边值得信赖的分类信息助手。";

export default function HoneyFooter() {
  const { siteName } = useSite();
  const { siteConfig } = useSiteConfig();
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  if (!siteConfig) return null;

  const footerInfo = siteConfig.footer_info || FOOTER_INFO_DEFAULT;
  const footerIcp = siteConfig.icp || "";
  const enableVip = siteConfig.enable_vip ?? true;
  const contactInfo = siteConfig.contact_info;

  // 组装默认菜单列（VIP 未开启时过滤开通入口）
  const menusA = DEFAULT_MENUS.filter((m) => m.href !== "/vip" || enableVip).slice(0, 4);
  const menusB = DEFAULT_MENUS.filter((m) => m.href !== "/vip" || enableVip).slice(4);

  const resolveHref = (item: any) => {
    const href = item.href || "#";
    if (typeof href === "string" && (href.startsWith("http") || href.startsWith("javascript"))) return href;
    return href;
  };

  const contactItems = [
    { type: "phone", icon: "ri-phone-line", text: contactInfo?.phone },
    { type: "wechat", icon: "ri-wechat-line", text: contactInfo?.wechat },
    { type: "email", icon: "ri-mail-line", text: contactInfo?.email },
    { type: "address", icon: "ri-map-pin-line", text: contactInfo?.address },
  ].filter((item) => item.text);

  return (
    <footer className="bg-[#23201A] text-[#FFF9EC]">
      {/* 琥珀收口线 */}
      <div className="h-1 bg-gradient-to-r from-[#F0A500] via-[#FFB520] to-[#F0A500]" />

      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-14 pb-8">
        <div className="grid gap-10 lg:grid-cols-4">
          {/* 品牌介绍 */}
          <div>
            <Link to="/" className="flex items-center gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl overflow-hidden bg-[#F0A500] text-[#23201A]">
                {logo && !logoError ? (
                  <img src={logo} alt={siteName} className="w-full h-full object-cover" onError={() => setLogoError(true)} />
                ) : (
                  <span className="text-base font-black">{(siteName || "S").charAt(0).toUpperCase()}</span>
                )}
              </span>
              {siteName && <span className="text-lg font-bold text-[#FFF9EC]">{siteName}</span>}
            </Link>
            {footerInfo && (
              <p className="mt-4 text-[13px] leading-6 text-[#B8B1A0]">
                {footerInfo}
              </p>
            )}
          </div>

          {/* 导航列一：快速导航 */}
          <div>
            <h3 className="text-sm font-bold text-[#F0A500] mb-4 flex items-center gap-1.5">
              <i className="ri-compass-3-line" />快速导航
            </h3>
            <ul className="space-y-2.5">
              {menusA.map((menu) => (
                <li key={menu.name}>
                  <Link to={resolveHref(menu)} className="text-[13px] text-[#D8D1BF] hover:text-[#F0A500] transition-colors">
                    {menu.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 导航列二：帮助支持 / 配置菜单 */}
          <div>
            <h3 className="text-sm font-bold text-[#F0A500] mb-4 flex items-center gap-1.5">
              <i className="ri-lifebuoy-line" />帮助支持
            </h3>
            <ul className="space-y-2.5">
              {menusB.map((menu) => (
                <li key={menu.name}>
                  <Link to={resolveHref(menu)} className="text-[13px] text-[#D8D1BF] hover:text-[#F0A500] transition-colors">
                    {menu.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 联系方式 */}
          <div>
            <h3 className="text-sm font-bold text-[#F0A500] mb-4 flex items-center gap-1.5">
              <i className="ri-customer-service-2-line" />联系方式
            </h3>
            {contactItems.length > 0 ? (
              <ul className="space-y-3">
                {contactItems.map((item) => (
                  <li key={item.type} className="flex items-start gap-2.5 text-[13px] text-[#D8D1BF]">
                    <i className={`${item.icon} text-[#F0A500] mt-0.5`} />
                    <span className="break-all">{item.text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-[#8B8577]">欢迎通过站点留言或后台联系我们</p>
            )}
          </div>
        </div>
      </div>

      {/* 版权条 */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-[#8B8577]">
            © {new Date().getFullYear()} {siteName || ""} 保留所有权利
          </p>
          {footerIcp && (
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noreferrer" className="text-xs text-[#8B8577] hover:text-[#F0A500] transition-colors">
              {footerIcp}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
